// App Store Guideline 5.1.2 Compliance: Clear data collection notice
// Users are informed about what data is collected during sign-up

import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { useTheme } from "@/hooks/useTheme";
import fitlinkLogo from "@/assets/fitlink-logo-new.png";
import { z } from "zod";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ComprehensiveOnboarding } from "@/components/ComprehensiveOnboarding";
import { Shield, Mail, Lock, User, UserCircle } from "lucide-react";
import { useLanguage } from "@/hooks/useLanguage";
import { motion } from "framer-motion";

const passwordSchema = z.string()
  .min(12, 'Password must be at least 12 characters')
  .regex(/[A-Z]/, 'Must contain at least one uppercase letter')
  .regex(/[a-z]/, 'Must contain at least one lowercase letter')
  .regex(/[0-9]/, 'Must contain at least one number')
  .regex(/[^A-Za-z0-9]/, 'Must contain at least one special character');

// Apple-style input wrapper component
const AppleInput = ({ 
  icon: Icon, 
  label, 
  ...props 
}: { 
  icon: React.ElementType; 
  label: string; 
} & React.ComponentProps<typeof Input>) => (
  <div className="space-y-2">
    <Label htmlFor={props.id} className="text-sm font-medium text-foreground/80">
      {label}
    </Label>
    <div className="relative">
      <div className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
        <Icon className="w-5 h-5" />
      </div>
      <Input
        {...props}
        className="h-12 pl-11 rounded-xl bg-background/60 border-border/50 backdrop-blur-sm 
                   focus:bg-background focus:border-primary/50 transition-all duration-200
                   placeholder:text-muted-foreground/50"
      />
    </div>
  </div>
);

const Auth = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { theme } = useTheme();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [resetPasswordOpen, setResetPasswordOpen] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingUserId, setOnboardingUserId] = useState<string>("");
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  useEffect(() => {
    // Check if user is already logged in
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        navigate("/dashboard");
      }
    });

    // Handle auth callbacks (email verification and password reset)
    const handleAuthCallback = async () => {
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const type = hashParams.get('type');
      
      if (type === 'recovery') {
        // User clicked password reset link
        setIsResettingPassword(true);
        toast({
          title: t("auth.setNewPassword"),
          description: t("auth.enterNewPasswordBelow"),
        });
      } else if (type === 'signup' || type === 'magiclink') {
        // User clicked email verification link - auto verify
        const { error } = await supabase.auth.getSession();
        if (!error) {
          toast({
            title: t("auth.emailVerified"),
            description: t("auth.emailVerifiedDesc"),
          });
          // Redirect to dashboard if logged in
          setTimeout(() => {
            supabase.auth.getSession().then(({ data: { session } }) => {
              if (session) {
                navigate("/dashboard");
              }
            });
          }, 1500);
        }
      }
    };

    handleAuthCallback();
  }, [navigate, toast, t]);

  const handleUpdatePassword = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);

    // Check if passwords match
    if (newPassword !== confirmPassword) {
      setLoading(false);
      toast({
        variant: "destructive",
        title: t("auth.passwordsDontMatch"),
        description: t("auth.passwordsDontMatchDesc"),
      });
      return;
    }

    // Validate password strength
    const passwordValidation = passwordSchema.safeParse(newPassword);
    if (!passwordValidation.success) {
      setLoading(false);
      toast({
        variant: "destructive",
        title: t("auth.passwordRequirementsNotMet"),
        description: passwordValidation.error.errors[0].message,
      });
      return;
    }

    const { error } = await supabase.auth.updateUser({
      password: newPassword
    });

    setLoading(false);

    if (error) {
      toast({
        variant: "destructive",
        title: t("common.error"),
        description: error.message,
      });
    } else {
      toast({
        title: t("auth.passwordUpdated"),
        description: t("auth.passwordUpdatedDesc"),
      });
      setIsResettingPassword(false);
      setNewPassword("");
      setConfirmPassword("");
      // Redirect to dashboard
      setTimeout(() => navigate("/dashboard"), 1500);
    }
  };

  const handleSignUp = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);

    const formData = new FormData(e.currentTarget);
    const email = formData.get("signup-email") as string;
    const password = formData.get("signup-password") as string;
    const username = formData.get("username") as string;
    const fullName = formData.get("full-name") as string;

    // Validate password strength
    const passwordValidation = passwordSchema.safeParse(password);
    if (!passwordValidation.success) {
      setLoading(false);
      toast({
        variant: "destructive",
        title: t("auth.passwordRequirementsNotMet"),
        description: passwordValidation.error.errors[0].message,
      });
      return;
    }

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/auth`,
        data: {
          username,
          full_name: fullName,
        },
      },
    });

    if (error) {
      setLoading(false);
      // Check if it's a unique constraint violation for username
      const isDuplicateUsername = error.message.includes('profiles_username_key') || 
                                   error.message.toLowerCase().includes('duplicate') ||
                                   error.message.toLowerCase().includes('username');
      
      toast({
        variant: "destructive",
        title: t("auth.signupFailed"),
        description: isDuplicateUsername 
          ? t("auth.usernameTaken")
          : error.message,
      });
      return;
    }

    // Send custom branded verification email
    try {
      await supabase.functions.invoke('send-auth-email', {
        body: {
          email,
          type: 'signup',
          redirectTo: `${window.location.origin}/auth`
        }
      });
    } catch (emailError) {
      console.error('Failed to send verification email:', emailError);
    }

    setLoading(false);

    // Show check email message
    toast({
      title: t("auth.checkYourEmail"),
      description: t("auth.verificationEmailSent"),
      duration: 10000,
    });
  };

  const handleSignIn = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);

    const formData = new FormData(e.currentTarget);
    const email = (formData.get("signin-email") as string)?.trim();
    const password = formData.get("signin-password") as string;
    // Validate inputs
    if (!email || email === '' || !password) {
      setLoading(false);
      toast({
        variant: "destructive",
        title: t("auth.missingCredentials"),
        description: !email ? t("auth.enterEmail") : t("auth.enterPassword"),
      });
      return;
    }


    // Check rate limit with server before attempting login (fail-secure)
    try {
      const { data: rateLimitCheck, error: rateLimitError } = await supabase.functions.invoke('check-auth-rate-limit', {
        body: { email }
      });

      if (rateLimitError || !rateLimitCheck?.allowed) {
        setLoading(false);
        toast({
          variant: "destructive",
          title: rateLimitCheck?.locked ? t("auth.accountLocked") : t("auth.tooManyAttempts"),
          description: rateLimitCheck?.message || t("auth.tryAgainLater"),
        });
        return;
      }
    } catch (error) {
      // FAIL-SECURE: Block login if rate limit service is unavailable
      console.error("Rate limit check failed:", error);
      setLoading(false);
      toast({
        variant: "destructive",
        title: t("auth.serviceUnavailable"),
        description: t("auth.serviceUnavailableDesc"),
      });
      return;
    }

    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    // Record the attempt
    await supabase.functions.invoke('record-auth-attempt', {
      body: { 
        email,
        successful: !error
      }
    });

    setLoading(false);

    if (error) {
      toast({
        variant: "destructive",
        title: t("auth.loginFailed"),
        description: error.message,
      });
    } else {
      navigate("/dashboard");
    }
  };

  const handlePasswordReset = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);

    const formData = new FormData(e.currentTarget);
    const email = formData.get("reset-email") as string;

    try {
      const { data, error } = await supabase.functions.invoke('send-auth-email', {
        body: {
          email,
          type: 'recovery',
          redirectTo: `${window.location.origin}/auth`
        }
      });

      setLoading(false);

      if (error) {
        toast({
          variant: "destructive",
          title: t("common.error"),
          description: error.message,
        });
      } else {
        toast({
          title: t("auth.checkYourEmail"),
          description: t("auth.resetLinkSent"),
          duration: 8000,
        });
        setResetPasswordOpen(false);
      }
    } catch (error: any) {
      setLoading(false);
      toast({
        variant: "destructive",
        title: t("common.error"),
        description: error.message || t("auth.failedToSendReset"),
      });
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-background via-background to-primary/5 p-4">
      <motion.div 
        className="w-full max-w-md"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
      >
        {/* Logo and branding */}
        <motion.div 
          className="text-center mb-10"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.1, duration: 0.4 }}
        >
          <div className="relative inline-block">
            <div className="absolute inset-0 bg-primary/20 blur-2xl rounded-full" />
            <img 
              src={fitlinkLogo} 
              alt="FITLINK" 
              className="relative w-20 h-20 mx-auto mb-5 rounded-[22px] shadow-elevated" 
            />
          </div>
          <h1 className="text-3xl font-bold tracking-tight text-foreground">
            FITLINK
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">{t("auth.tagline")}</p>
        </motion.div>

        {isResettingPassword ? (
          <motion.div
            className="bg-card/80 backdrop-blur-xl rounded-3xl p-8 shadow-elevated border border-border/30"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="text-center mb-6">
              <h2 className="text-xl font-semibold text-foreground">{t("auth.setNewPassword")}</h2>
              <p className="text-sm text-muted-foreground mt-1">{t("auth.enterNewPasswordBelow")}</p>
            </div>
            
            <form onSubmit={handleUpdatePassword} className="space-y-5">
              <AppleInput
                icon={Lock}
                label={t("auth.newPassword")}
                id="new-password"
                type="password"
                placeholder={t("auth.enterNewPassword")}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
              />
              <p className="text-xs text-muted-foreground -mt-3 ml-1">
                {t("auth.passwordRequirements")}
              </p>
              
              <AppleInput
                icon={Lock}
                label={t("auth.confirmNewPassword")}
                id="confirm-password"
                type="password"
                placeholder={t("auth.confirmYourPassword")}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
              
              <Button 
                type="submit" 
                className="w-full h-12 rounded-xl text-base font-medium shadow-lg shadow-primary/20" 
                disabled={loading}
              >
                {loading ? t("auth.updating") : t("auth.confirmNewPasswordBtn")}
              </Button>
            </form>
          </motion.div>
        ) : (
          <motion.div
            className="bg-card/80 backdrop-blur-xl rounded-3xl p-8 shadow-elevated border border-border/30"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div className="text-center mb-6">
              <h2 className="text-xl font-semibold text-foreground">{t("auth.welcome")}</h2>
              <p className="text-sm text-muted-foreground mt-1">{t("auth.signInOrCreateAccount")}</p>
            </div>

            <Tabs defaultValue="signin" className="w-full">
              <TabsList className="grid w-full grid-cols-2 h-11 p-1 mb-6 bg-muted/50 rounded-xl">
                <TabsTrigger 
                  value="signin" 
                  className="rounded-lg text-sm font-medium data-[state=active]:bg-background data-[state=active]:shadow-sm transition-all"
                >
                  {t("auth.signIn")}
                </TabsTrigger>
                <TabsTrigger 
                  value="signup"
                  className="rounded-lg text-sm font-medium data-[state=active]:bg-background data-[state=active]:shadow-sm transition-all"
                >
                  {t("auth.signUp")}
                </TabsTrigger>
              </TabsList>

              <TabsContent value="signin" className="mt-0">
                <form onSubmit={handleSignIn} className="space-y-5">
                  <AppleInput
                    icon={Mail}
                    label={t("auth.email")}
                    id="signin-email"
                    name="signin-email"
                    type="email"
                    placeholder="you@example.com"
                    required
                  />
                  
                  <AppleInput
                    icon={Lock}
                    label={t("auth.password")}
                    id="signin-password"
                    name="signin-password"
                    type="password"
                    placeholder="••••••••"
                    required
                  />
                  
                  <Button 
                    type="submit" 
                    className="w-full h-12 rounded-xl text-base font-medium shadow-lg shadow-primary/20" 
                    disabled={loading}
                  >
                    {loading ? t("auth.signingIn") : t("auth.signIn")}
                  </Button>
                  
                  <Dialog open={resetPasswordOpen} onOpenChange={setResetPasswordOpen}>
                    <DialogTrigger asChild>
                      <button 
                        type="button"
                        className="w-full text-center text-sm text-primary hover:text-primary/80 transition-colors py-2"
                      >
                        {t("auth.forgotPassword")}
                      </button>
                    </DialogTrigger>
                    <DialogContent className="rounded-2xl">
                      <DialogHeader>
                        <DialogTitle>{t("auth.resetPassword")}</DialogTitle>
                        <DialogDescription>
                          {t("auth.resetPasswordDesc")}
                        </DialogDescription>
                      </DialogHeader>
                      <form onSubmit={handlePasswordReset} className="space-y-4 mt-2">
                        <AppleInput
                          icon={Mail}
                          label={t("auth.email")}
                          id="reset-email"
                          name="reset-email"
                          type="email"
                          placeholder="you@example.com"
                          required
                        />
                        <Button type="submit" className="w-full h-11 rounded-xl" disabled={loading}>
                          {loading ? t("auth.sending") : t("auth.sendResetLink")}
                        </Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                </form>
              </TabsContent>

              <TabsContent value="signup" className="mt-0">
                <form onSubmit={handleSignUp} className="space-y-4">
                  <AppleInput
                    icon={UserCircle}
                    label={t("auth.fullName")}
                    id="full-name"
                    name="full-name"
                    type="text"
                    placeholder="John Doe"
                    required
                  />
                  
                  <AppleInput
                    icon={User}
                    label={t("auth.username")}
                    id="username"
                    name="username"
                    type="text"
                    placeholder={t("auth.usernamePlaceholder")}
                    required
                  />
                  
                  <AppleInput
                    icon={Mail}
                    label={t("auth.email")}
                    id="signup-email"
                    name="signup-email"
                    type="email"
                    placeholder="you@example.com"
                    required
                  />
                  
                  <div>
                    <AppleInput
                      icon={Lock}
                      label={t("auth.password")}
                      id="signup-password"
                      name="signup-password"
                      type="password"
                      placeholder={t("auth.passwordPlaceholder")}
                      minLength={12}
                      required
                    />
                    <p className="text-xs text-muted-foreground mt-2 ml-1">
                      {t("auth.passwordHint")}
                    </p>
                  </div>
                  
                  <Button 
                    type="submit" 
                    className="w-full h-12 rounded-xl text-base font-medium shadow-lg shadow-primary/20 mt-2" 
                    disabled={loading}
                  >
                    {loading ? t("auth.creatingAccount") : t("auth.createAccount")}
                  </Button>

                  {/* Privacy Notice - App Store Guideline 5.1.2 Compliance */}
                  <div className="mt-4 p-4 bg-muted/30 rounded-2xl border border-border/30">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Shield className="w-4 h-4 text-primary" />
                      </div>
                      <div className="text-xs text-muted-foreground">
                        <p className="font-medium text-foreground mb-1">{t("auth.dataPrivacy")}</p>
                        <p className="leading-relaxed">
                          {t("auth.dataPrivacyDesc")}
                        </p>
                      </div>
                    </div>
                  </div>
                </form>
              </TabsContent>
            </Tabs>
          </motion.div>
        )}

        <motion.div 
          className="mt-8 text-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
        >
          <div className="flex justify-center gap-4 text-sm text-muted-foreground">
            <Link to="/?terms=true" className="hover:text-primary transition-colors">
              {t("auth.termsOfUse")}
            </Link>
            <span className="text-border">•</span>
            <Link to="/privacy" className="hover:text-primary transition-colors">
              {t("auth.privacyPolicy")}
            </Link>
            <span className="text-border">•</span>
            <Link to="/support" className="hover:text-primary transition-colors">
              {t("nav.support")}
            </Link>
          </div>
        </motion.div>
      </motion.div>

      {showOnboarding && (
        <ComprehensiveOnboarding
          open={showOnboarding}
          userId={onboardingUserId}
          onComplete={() => {
            setShowOnboarding(false);
            navigate("/dashboard");
          }}
        />
      )}
    </div>
  );
};

export default Auth;
